"use client";

import { OspToast } from "@ospweb/sdk-ui";
import { authCacheManager } from "@/src/features/auth/compat/auth-cache-compat";
import log from "logbeacon";
import { useMutation } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { OWNER_NICK_NAME_MAX_LENGTH } from "../constants/ownerNickName";
import { rootStore } from "../store";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { setEdgenOnboardingStepCookie } from "@/src/utils/onboarding";
import {
  isOnboardingHarnessEnabled,
  parseOnboardingHarnessQuery,
} from "@/src/hooks/onboarding/onboardingHarnessQuery";

export { OWNER_NICK_NAME_MAX_LENGTH } from "../constants/ownerNickName";

const SAVE_NICK_NAME_ERROR_TOAST =
  "Failed to save your name. Please try again.";

function shouldBypassNickNameApiForHarness(): boolean {
  if (!isOnboardingHarnessEnabled() || typeof window === "undefined") {
    return false;
  }
  const harness = parseOnboardingHarnessQuery(
    new URLSearchParams(window.location.search),
  );
  return harness.enabled && harness.auth === "onboarding";
}

function patchOwnerNickNameInStore(nickName: string) {
  const ownerInfo = rootStore.user.ownerInfo;
  if (!ownerInfo?.id) {
    return;
  }

  const nextOwnerInfo = { ...ownerInfo, nick_name: nickName };
  rootStore.user.ownerInfo = nextOwnerInfo;
  authCacheManager.saveOwnerInfoCache(nextOwnerInfo, ownerInfo.id);
}

/**
 * Persist Ed's display nickname via POST /owners/{owner_id}/setting.
 */
export const useSaveOwnerNickName = () => {
  const { memberApi } = useOspApiClientProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";

  const saveNickName = async (nickName: string) => {
    const trimmed = nickName.trim();
    if (!trimmed) {
      throw new Error("nick_name is required");
    }
    if (trimmed.length > OWNER_NICK_NAME_MAX_LENGTH) {
      throw new Error("nick_name exceeds max length");
    }
    if (shouldBypassNickNameApiForHarness()) {
      return { nickName: trimmed, owner: undefined };
    }
    if (!memberApi) {
      throw new Error("API client is not available");
    }
    if (!isLoggedIn || !ownerId) {
      throw new Error("You need to be logged in to save your name");
    }

    const res = await memberApi.owners.updateOwnerSetting(ownerId, {
      nick_name: trimmed,
    });
    return { nickName: trimmed, owner: res?.data?.data };
  };

  const {
    mutate,
    mutateAsync,
    isPending: isSaving,
    error,
    isError,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: saveNickName,
    onSuccess: ({ nickName }) => {
      patchOwnerNickNameInStore(nickName);
      setEdgenOnboardingStepCookie(2);
      log.debug("[onboarding] owner nick_name saved", { nickName });
    },
    onError: (mutationError: Error) => {
      log.error("[onboarding] save owner nick_name failed", mutationError);
      OspToast.error({ content: SAVE_NICK_NAME_ERROR_TOAST });
    },
  });

  return {
    saveNickName: mutate,
    saveNickNameAsync: mutateAsync,
    isSaving,
    error,
    isError,
    isSuccess,
    reset,
  };
};
